<template>
    <div>
        <div class="row">
            <div class="col-12 p-3">
                <el-form ref="ring-group-availability"
                         label-position="top"
                         :model="ring_group_availability"
                         :rules="rules_ring_group_availability"
                         @submit.prevent.native="checkRingGroupAvailability">
                    <el-form-item label="Enter an inbox ID:">
                        <el-input class="width-200 p-0"
                                  placeholder="1234"
                                  v-model="ring_group_availability.ring_group_id"
                                  @input="preValidateForm('ring-group-availability')">
                        </el-input>
                        <el-button type="success"
                                   class="p-x-md"
                                   :loading="loading_availability_btn"
                                   :disabled="!validated"
                                   @click="checkRingGroupAvailability">
                            Check Availability
                        </el-button>
                    </el-form-item>
                </el-form>
            </div>
        </div>
        <div class="row"
             v-if="has_result">
            <div class="col-md-6">
                <strong>Formatted response:</strong>
                <p class="text-danger mt-2"
                   v-if="availability_result.error">
                    <strong>Error!</strong> {{ availability_result.error }}
                </p>
                <div v-if="availability_result.test_results">
                    <p class="mt-2 mb-2">
                        <strong>Available Users:</strong>
                        {{ availability_result.available_users_count }}
                    </p>
                    <p class="mb-2">
                        <strong>Unavailable Users:</strong>
                        {{ availability_result.unavailable_users_count }}
                    </p>
                    <p class="mb-2">
                        <strong>Total Users:</strong>
                        {{ availability_result.total_users_count }}
                    </p>
                    <div v-if="availability_result.test_results.length > 0">
                        <strong>User Details:</strong>
                        <div v-for="user in availability_result.test_results" :key="user.id" class="mt-2 p-2 border-left border-success" :class="{ 'border-danger': user.failed_to_route }">
                            <p class="mb-1"><strong>{{ user.name }}</strong> (ID: {{ user.id }})</p>
                            <p class="mb-1 text-sm" v-if="user.email">Email: {{ user.email }}</p>
                            <p class="mb-1 text-sm" :class="user.failed_to_route ? 'text-danger' : 'text-success'">
                                Status: {{ user.failed_to_route ? 'Unavailable' : 'Available' }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <strong>Response body:</strong>
                <pre v-highlightjs><code class="json">{{ availability_result }}</code></pre>
            </div>
        </div>
        <div class="row b-t b-b">
            <div class="col-12 p-3">
                <company-form-token></company-form-token>
            </div>
        </div>
        <div class="row">
            <div class="col-12 p-3">
                <div class="mb-2">
                    <strong class="integration-api-titles">Inbox Availability API</strong>
                </div>

                <p class="text-md">
                    <span class="text-dark-greenish _600">GET /api/v1/webhook/ring-group/availability</span>
                </p>

                <p>
                    <strong>Required Fields:</strong>
                    <br>
                    <span class="text-dark-greenish _600">api_token</span>,
                    <span class="text-dark-greenish _600">ring_group_id</span>
                </p>

                <p>
                    <strong>Example GET URL:</strong>
                    <br>
                    <span class="text-dark-greenish _600">{{ statics.domain | fixDomain }}/api/v1/webhook/ring-group/availability?api_token={{ companyToken }}&ring_group_id=1</span>
                </p>

                <p class="mb-2">
                    <strong>Example GET header:</strong>
                </p>
                <pre v-highlightjs><code class="json">{
    "Accept": "application/json",
    "Content-Type": "application/json"
}</code></pre>

                <p class="mb-2">
                    <strong>Returns an HTTP 200 response on success, Example success response:
                    </strong>
                </p>
                <pre v-highlightjs><code class="json">{
  "test_results": [
    {
      "id": 2,
      "failed_to_route": false,
      "order": 0,
      "name": "John Agent",
      "email": "john.agent@company.com",
      "reasons": {
        "is_active": true,
        "takes_task_from_area_code": true,
        "takes_task_in_state": true,
        "takes_task_in_operating_hours": true,
        "takes_call_within_concurrency_limit": true,
        "takes_call_within_quota_limit": true,
        "takes_call_within_duplication_period_limit": true,
        "agent_answers_call": true,
        "agent_accepts_call": true
      },
      "answer_by": "Browser / Apps",
      "agent_status": "Accepting Calls"
    },
    {
      "id": 3,
      "failed_to_route": true,
      "order": 1,
      "name": "Jane Agent",
      "email": "jane.agent@company.com",
      "reasons": {
        "is_active": true,
        "takes_task_from_area_code": true,
        "takes_task_in_state": true,
        "takes_task_in_operating_hours": false,
        "takes_call_within_concurrency_limit": true,
        "takes_call_within_quota_limit": true,
        "takes_call_within_duplication_period_limit": true,
        "agent_answers_call": true,
        "agent_accepts_call": true
      },
      "answer_by": "Phone Number",
      "agent_status": "On Break"
    }
  ],
  "available_users_count": 1,
  "unavailable_users_count": 1,
  "total_users_count": 2
}</code></pre>

                <p class="mb-2">
                    <strong>Returns an HTTP 400 on validation failure, Example failure response:</strong>
                </p>
                <pre v-highlightjs><code class="json">{
    "message": "The given data was invalid.",
    "errors": {
        "ring_group_id": [
            "The inbox id field is required."
        ]
    }
}</code></pre>

                <p class="mb-2">
                    <strong>Returns an HTTP 404 if inbox is not found, Example failure response:</strong>
                </p>
                <pre v-highlightjs><code class="json">{
    "error": "Inbox not found."
}</code></pre>

                <p class="mb-2">
                    <strong>Response Fields Explanation:</strong>
                </p>
                <ul class="mb-3">
                    <li><strong>test_results:</strong> Array of user objects with availability details</li>
                    <li><strong>available_users_count:</strong> Number of users who can take calls</li>
                    <li><strong>unavailable_users_count:</strong> Number of users who cannot take calls</li>
                    <li><strong>total_users_count:</strong> Total number of users in the inbox</li>
                    <li><strong>failed_to_route:</strong> Boolean indicating if the user can take calls (false = available)</li>
                    <li><strong>agent_status:</strong> Human-readable string representing the current agent status (e.g., "Accepting Calls", "On Break", "Offline", "On Call", etc.)</li>
                    <li><strong>reasons:</strong> Object containing detailed availability checks for each user</li>
                </ul>

                <p class="mb-2">
                    <strong>Detailed Availability Tests Explanation:</strong>
                </p>
                <p class="mb-3 text-muted">
                    Each user in the inbox undergoes a series of availability tests. The <code>reasons</code> object contains the results of these tests:
                </p>
                
                <div class="mb-4">
                    <h5 class="text-dark-greenish mb-2">Rule 1: Agent Active Status</h5>
                    <p class="mb-2"><strong>Field:</strong> <code>is_active</code></p>
                    <p class="mb-2"><strong>Description:</strong> Checks if the agent's account is active and not paused.</p>
                </div>

                <div class="mb-4">
                    <h5 class="text-dark-greenish mb-2">Rule 2: Area Code Coverage</h5>
                    <p class="mb-2"><strong>Field:</strong> <code>takes_task_from_area_code</code></p>
                    <p class="mb-2"><strong>Description:</strong> Verifies if the agent is configured to handle calls from the caller's area code.</p>
                </div>

                <div class="mb-4">
                    <h5 class="text-dark-greenish mb-2">Rule 3: Geographic Coverage</h5>
                    <p class="mb-2"><strong>Field:</strong> <code>takes_task_in_state</code></p>
                    <p class="mb-2"><strong>Description:</strong> Checks if the agent handles calls from the caller's country and state.</p>
                </div>

                <div class="mb-4">
                    <h5 class="text-dark-greenish mb-2">Rule 4: Operating Hours</h5>
                    <p class="mb-2"><strong>Field:</strong> <code>takes_task_in_operating_hours</code></p>
                    <p class="mb-2"><strong>Description:</strong> Validates if the current time falls within the agent's configured working hours.</p>
                </div>

                <div class="mb-4">
                    <h5 class="text-dark-greenish mb-2">Rule 5: Device Capability</h5>
                    <p class="mb-2"><strong>Field:</strong> <code>agent_answers_call</code></p>
                    <p class="mb-2"><strong>Description:</strong> Ensures the agent's device/setup can receive calls.</p>
                </div>

                <div class="mb-4">
                    <h5 class="text-dark-greenish mb-2">Rule 6: Agent Availability Status</h5>
                    <p class="mb-2"><strong>Field:</strong> <code>agent_accepts_call</code></p>
                    <p class="mb-2"><strong>Description:</strong> Checks if the agent's current availability status allows accepting new calls.</p>
                </div>

                <div class="mb-4">
                    <h5 class="text-dark-greenish mb-2">Rule 7: Concurrency Limits</h5>
                    <p class="mb-2"><strong>Field:</strong> <code>takes_call_within_concurrency_limit</code></p>
                    <p class="mb-2"><strong>Description:</strong> Verifies the agent hasn't exceeded their maximum concurrent call limit.</p>
                </div>

                <div class="alert alert-info mb-4">
                    <strong>Note:</strong> An agent is considered <strong>available</strong> (failed_to_route = false) only when ALL applicable tests return true. 
                    If any test fails, the agent is marked as unavailable (failed_to_route = true) and won't receive calls from the inbox.
                </div>

                <p class="_600"
                   v-if="!statics.whitelabel">
                    If you need more API functions, please contact our support at support@aloware.com .
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { company_token_mixin, form_validation_mixin, validator_mixin } from '../mixins'
import CompanyFormToken from './account/company-form-token'

export default {
    mixins: [
        validator_mixin,
        form_validation_mixin,
        company_token_mixin
    ],

    components: {
        CompanyFormToken
    },

    data() {
        return {
            rules_ring_group_availability: {
                ring_group_id: {
                    validator: this.ringGroupIdValidator,
                    trigger: 'blur'
                }
            },
            ring_group_availability: {
                ring_group_id: null
            },
            statics: {
                logo: null,
                logo_inverse: null,
                logo_square: null,
                logo_square_inverse: null,
                host: null,
                referer: null,
                name: null,
                domain: null,
                whitelabel: false,
                path: null
            },
            loading_whitelabel: true,
            loading_availability_btn: false,
            availability_result: {},
            has_result: false
        }
    },

    computed: {
        ...mapState('cache', ['current_company']),
        
    },

    created() {
        this.getStatics()
    },

    methods: {
        getStatics() {
            this.loading_whitelabel = true
            axios.get('/get-statics')
                .then(res => {
                    this.statics = res.data
                    this.loading_whitelabel = false
                })
                .catch(err => {
                    console.log(err)
                    this.$root.handleErrors(err.response)
                    this.loading_whitelabel = false
                })
        },

        ringGroupIdValidator(rule, value, callback) {
            if (!value) {
                callback(new Error('Inbox ID is required'))
            } else if (!/^\d+$/.test(value)) {
                callback(new Error('Inbox ID must be a number'))
            } else {
                callback()
            }
        },

        checkRingGroupAvailability() {
            this.has_result = false
            if (this.validateForm('ring-group-availability') == true) {
                this.loading_availability_btn = true
                this.availability_result = {}
                
                const params = new URLSearchParams({
                    'ring_group_id': this.ring_group_availability.ring_group_id,
                    'api_token': this.current_company.form_capture_token
                })
                
                axios.get(`api/v1/webhook/ring-group/availability?${params.toString()}`).then(res => {
                    this.loading_availability_btn = false
                    this.availability_result = res.data
                    this.has_result = true
                    this.resetRingGroupAvailability()
                }).catch(err => {
                    console.log(err)
                    this.availability_result = err.response.data
                    this.has_result = true
                    this.loading_availability_btn = false
                    this.resetRingGroupAvailability()
                })
            }

            return false
        },

        resetRingGroupAvailability() {
            this.ring_group_availability.ring_group_id = null
            this.validated = false
        }
    }
}
</script>
